/**
 * Location page AV Chart deep linking (#reg-flow + marketing course_id / interest).
 * Marketing query params are mapped to Pulse AVC params on data-page: prodid, gnrid.
 */

export const REG_FLOW_HASH = '#reg-flow'

/** Set when marketing `#reg-flow` was cleared so Pulse loads filters from `data-page`. */
export const REG_FLOW_PENDING_STORAGE_KEY = 'location-reg-flow-hash-pending'

/** Pulse `data-page` route kept while marketing hash is pending (e.g. `/reg-flow/avail-charts-lock?lid=…`). */
export const REG_FLOW_CHART_ROUTE_STORAGE_KEY = 'location-reg-flow-chart-route'

/** SSR meta tag consumed by `/location-reg-flow-bootstrap.js` (route.hash is unavailable on server). */
export const REG_FLOW_CHART_ROUTE_META_NAME = 'location-reg-flow-chart-route'


const LOCATION_DETAIL_PATH = /^\/locations\/[^/]+\/[^/]+$/

export function isLocationDetailPathname(pathname: string): boolean {
  const path = String(pathname ?? '').replace(/\/$/, '') || '/'
  return LOCATION_DETAIL_PATH.test(path)
}

function readPulseChartRouteFromMeta(): string {
  if (typeof document === 'undefined') return ''
  const meta = document.querySelector(`meta[name="${REG_FLOW_CHART_ROUTE_META_NAME}"]`)
  const content = meta?.getAttribute('content')?.trim() ?? ''
  if (!content) return ''
  return content.startsWith('/') ? content : `/${content}`
}

/**
 * Pulse: hash wins over `data-page`. For filtered deep links, set the route on
 * `#www-spa-container` `data-page` and clear the hash so Pulse auto-fills it on first load.
 */
export function prepareRegFlowHashForPulseInit(options: {
  pathname?: string
  hash?: string
  pulseChartRoute?: string
} = {}): boolean {
  if (typeof window === 'undefined') return false

  const pathname = options.pathname ?? window.location.pathname
  const hash = options.hash ?? window.location.hash
  if (!isLocationDetailPathname(pathname)) return false
  if (!isRegFlowHash(hash)) return false

  const pulseChartRoute =
    options.pulseChartRoute
    ?? sessionStorage.getItem(REG_FLOW_CHART_ROUTE_STORAGE_KEY)
    ?? readPulseChartRouteFromMeta()
    ?? ''
  if (!pulseChartRoute) return false

  const normalizedRoute = normalizeAvailChartsLockRoute(pulseChartRoute)
  sessionStorage.setItem(REG_FLOW_PENDING_STORAGE_KEY, '1')
  sessionStorage.setItem(REG_FLOW_CHART_ROUTE_STORAGE_KEY, normalizedRoute)

  const nextUrl = `${window.location.pathname}${window.location.search}`
  window.history.replaceState(window.history.state, '', nextUrl)
  return true
}

/** Synchronous head script: stash chart route, clear `#reg-flow` hash before Nuxt/Pulse load. */
export function buildRegFlowBootstrapScript(pulseChartRoute: string): string {
  const normalizedRoute = normalizeAvailChartsLockRoute(pulseChartRoute)
  const routeJson = JSON.stringify(normalizedRoute)

  return `(function(){try{
    var path=(location.pathname||'').replace(/\\/$/,'')||'/';
    if(!/^\\/locations\\/[^/]+\\/[^/]+$/.test(path))return;
    var h=(location.hash||'').trim().toLowerCase();
    if(h!=='#reg-flow'&&h!=='reg-flow')return;
    var route=${routeJson};
    sessionStorage.setItem('${REG_FLOW_PENDING_STORAGE_KEY}','1');
    sessionStorage.setItem('${REG_FLOW_CHART_ROUTE_STORAGE_KEY}',route);
    history.replaceState(history.state,'',location.pathname+location.search);
  }catch(e){}})();`
}

/** @deprecated Use prepareRegFlowHashForPulseInit */
export function stripRegFlowHashForPulseInit(
  options: { pathname?: string; hash?: string; pulseChartRoute?: string } = {},
): boolean {
  return prepareRegFlowHashForPulseInit(options)
}

export function hasRegFlowDeepLinkPending(): boolean {
  if (typeof window === 'undefined') return false
  return sessionStorage.getItem(REG_FLOW_PENDING_STORAGE_KEY) === '1'
}

/** Do not restore `#reg-flow` while Pulse is mounted — bare hash routes to choose-locations. */
export function clearRegFlowDeepLinkPending(): void {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(REG_FLOW_PENDING_STORAGE_KEY)
}

/** @deprecated Restoring `#reg-flow` re-triggers Pulse → choose-locations. Use clearRegFlowDeepLinkPending. */
export function restoreRegFlowMarketingHash(): boolean {
  clearRegFlowDeepLinkPending()
  return false
}

/** Matches Bootstrap `md` breakpoint used by `LocationAVC` (`d-none d-md-block`). */
export const LOCATION_AV_CHART_MIN_VIEWPORT_WIDTH = 768

/** Canonical interest names aligned with course catalog / chart UI. */
export const CANONICAL_INTEREST_NAMES = [
  'Coding',
  'Artificial Intelligence',
  'Game Dev',
  'Robotics',
  'Creative',
  'Math',
  '3D Printing',
] as const

/** Pulse AVC genre filter ids (`gnrid`) aligned with course catalog type IDs. */
export const INTEREST_NAME_TO_GNRID: Record<(typeof CANONICAL_INTEREST_NAMES)[number], number> = {
  Coding: 295000,
  'Artificial Intelligence': 295009,
  'Game Dev': 295002,
  Robotics: 295001,
  Creative: 295003,
  Math: 295006,
  '3D Printing': 295010,
}

export type LocationAvChartDeepLinkInput = {
  hash: string
  queryCourseId: unknown
  queryInterest: unknown
  locationPulseId: number | null | undefined
  locationCourses: unknown
  locationGenres: unknown
  /** Open location with embedded chart (`status.typeId === 1`). */
  hasEmbeddedChart: boolean
  viewportWidth: number
}

export type LocationAvChartDeepLinkResult = {
  isRegFlowDeepLink: boolean
  shouldScrollToChart: boolean
  dataPage: string
  appliedCourseId: number | null
  appliedInterest: string | null
}

export function isRegFlowHash(hash: string): boolean {
  const normalized = String(hash ?? '').trim().toLowerCase()
  return normalized === '#reg-flow' || normalized === 'reg-flow'
}

/** Pulse embedded chart hash after marketing `#reg-flow` is swapped (may include query params). */
export function isAvailChartsLockHash(hash: string): boolean {
  const normalized = String(hash ?? '').trim().toLowerCase()
  return normalized.startsWith('#/reg-flow/avail-charts-lock')
}

export function extractAvailChartsLockFilters(routePath: string): {
  prodid: number | null
  gnrid: number | null
} {
  const normalized = normalizeAvailChartsLockRoute(routePath)
  const qIndex = normalized.indexOf('?')
  if (qIndex === -1) return { prodid: null, gnrid: null }
  const params = new URLSearchParams(normalized.slice(qIndex + 1))
  return {
    prodid: parsePositiveProductId(params.get('prodid')),
    gnrid: parsePositiveProductId(params.get('gnrid')),
  }
}

/** Re-apply filter params when Pulse drops them from the location hash. */
export function mergeAvailChartsLockFilterRoute(
  currentRoute: string,
  targetRoute: string,
  locationPulseId: number | null | undefined,
): string {
  const lid = parsePositiveProductId(locationPulseId)
  if (lid == null) return normalizeAvailChartsLockRoute(currentRoute)

  const current = normalizeAvailChartsLockRoute(currentRoute)
  const target = normalizeAvailChartsLockRoute(targetRoute)
  const currentFilters = extractAvailChartsLockFilters(current)
  const targetFilters = extractAvailChartsLockFilters(target)

  if (targetFilters.prodid != null && currentFilters.prodid == null) {
    return buildAvailChartsLockDataPage(lid, { prodid: targetFilters.prodid })
  }
  if (
    targetFilters.gnrid != null
    && currentFilters.gnrid == null
    && currentFilters.prodid == null
  ) {
    return buildAvailChartsLockDataPage(lid, { gnrid: targetFilters.gnrid })
  }

  return current
}

export function parsePositiveProductId(value: unknown): number | null {
  if (Array.isArray(value)) {
    for (const item of value) {
      const parsed = parsePositiveProductId(item)
      if (parsed != null) return parsed
    }
    return null
  }
  if (value == null) return null
  const trimmed = String(value).trim()
  if (!/^\d+$/.test(trimmed)) return null
  const n = Number(trimmed)
  if (!Number.isSafeInteger(n) || n <= 0) return null
  return n
}

export function normalizeInterestName(
  value: unknown,
  allowedNames: readonly string[] = CANONICAL_INTEREST_NAMES,
): string | null {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  return allowedNames.find((name) => name.toLowerCase() === raw.toLowerCase()) ?? null
}

function extractProductIdFromCourseEntry(entry: unknown): number | null {
  if (typeof entry === 'number' && Number.isSafeInteger(entry) && entry > 0) {
    return entry
  }
  if (entry && typeof entry === 'object') {
    const record = entry as Record<string, unknown>
    for (const key of ['id', 'pulseId', 'productId', 'courseId']) {
      const parsed = parsePositiveProductId(record[key])
      if (parsed != null) return parsed
    }
  }
  return null
}

export function locationOffersProductId(courses: unknown, productId: number): boolean {
  if (!Array.isArray(courses) || courses.length === 0) {
    return true
  }
  return courses.some((entry) => extractProductIdFromCourseEntry(entry) === productId)
}

function extractGenreName(entry: unknown): string | null {
  if (typeof entry === 'string') {
    return normalizeInterestName(entry)
  }
  if (entry && typeof entry === 'object') {
    const record = entry as Record<string, unknown>
    return (
      normalizeInterestName(record.name)
      ?? normalizeInterestName(record.typeString)
    )
  }
  return null
}

function extractGenreId(entry: unknown): number | null {
  if (typeof entry === 'number') {
    return parsePositiveProductId(entry)
  }
  if (entry && typeof entry === 'object') {
    const record = entry as Record<string, unknown>
    for (const key of ['typeID', 'typeId', 'id', 'gnrid', 'genreId']) {
      const parsed = parsePositiveProductId(record[key])
      if (parsed != null) return parsed
    }
  }
  return null
}

/** Resolve Pulse `gnrid` for a canonical interest name (location genre entry, then static map). */
export function resolveGenreIdForInterest(
  interestName: string,
  genres: unknown,
): number | null {
  const target = interestName.toLowerCase()
  if (Array.isArray(genres)) {
    for (const entry of genres) {
      const name = extractGenreName(entry)
      if (name != null && name.toLowerCase() === target) {
        const genreId = extractGenreId(entry)
        if (genreId != null) return genreId
      }
    }
  }

  const canonicalName = normalizeInterestName(interestName)
  if (canonicalName == null) return null
  return INTEREST_NAME_TO_GNRID[canonicalName] ?? null
}

export function locationOffersInterest(
  genres: unknown,
  interestName: string,
): boolean {
  if (!Array.isArray(genres) || genres.length === 0) {
    return true
  }
  const target = interestName.toLowerCase()
  const targetGnrid = resolveGenreIdForInterest(interestName, null)
  return genres.some((entry) => {
    const name = extractGenreName(entry)
    if (name != null && name.toLowerCase() === target) return true
    if (targetGnrid != null && extractGenreId(entry) === targetGnrid) return true
    return false
  })
}

export function buildAvailChartsLockDataPage(
  locationPulseId: number,
  filter: { prodid?: number | null; gnrid?: number | null } = {},
): string {
  const params = new URLSearchParams()
  params.set('lid', String(locationPulseId))
  if (filter.prodid != null) {
    params.set('prodid', String(filter.prodid))
  } else if (filter.gnrid != null) {
    params.set('gnrid', String(filter.gnrid))
  }
  params.set('rgnad', 'true')
  return normalizeAvailChartsLockRoute(`/reg-flow/avail-charts-lock?${params.toString()}`)
}

const FILTER_PARAM_KEYS = new Set([
  'lid',
  'lid[]',
  'prodid',
  'prodid[]',
  'course_id',
  'gnrid',
  'gnrid[]',
  'interest',
  'rgnad',
])

/** Collapse Pulse `lid[]` / `prodid[]` / `gnrid[]` → scalar params; map legacy keys to Pulse names. */
export function normalizeAvailChartsLockRoute(routePath: string): string {
  const trimmed = String(routePath ?? '').trim()
  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  if (!withLeadingSlash.startsWith('/reg-flow/avail-charts-lock')) {
    return withLeadingSlash
  }

  const qIndex = withLeadingSlash.indexOf('?')
  if (qIndex === -1) return withLeadingSlash

  const path = withLeadingSlash.slice(0, qIndex)
  const params = new URLSearchParams(withLeadingSlash.slice(qIndex + 1))

  let lid: number | null = null
  let prodid: number | null = null
  let gnrid: number | null = null

  for (const [key, value] of params.entries()) {
    if (key === 'lid' || key === 'lid[]') {
      if (lid == null) lid = parsePositiveProductId(value)
    } else if (key === 'prodid' || key === 'prodid[]' || key === 'course_id') {
      if (prodid == null) prodid = parsePositiveProductId(value)
    } else if (key === 'gnrid' || key === 'gnrid[]') {
      if (gnrid == null) gnrid = parsePositiveProductId(value)
    } else if (key === 'interest' && gnrid == null) {
      const interestName = normalizeInterestName(value)
      if (interestName != null) {
        gnrid = resolveGenreIdForInterest(interestName, null)
      }
    }
  }

  const normalized = new URLSearchParams()
  if (lid != null) normalized.set('lid', String(lid))
  if (prodid != null) {
    normalized.set('prodid', String(prodid))
  } else if (gnrid != null) {
    normalized.set('gnrid', String(gnrid))
  }

  for (const [key, value] of params.entries()) {
    if (FILTER_PARAM_KEYS.has(key)) continue
    if (key === 'did' || key === 'ageid') {
      const parsed = parsePositiveProductId(value)
      if (parsed != null) normalized.set(key, String(parsed))
    } else {
      normalized.set(key, value)
    }
  }

  if (!normalized.has('rgnad')) normalized.set('rgnad', 'true')

  const query = normalized.toString()
  return query ? `${path}?${query}` : path
}

function parseLidFromAvailChartsRoute(routePath: string): number | null {
  const normalized = normalizeAvailChartsLockRoute(routePath)
  const qIndex = normalized.indexOf('?')
  if (qIndex === -1) return null
  return parsePositiveProductId(new URLSearchParams(normalized.slice(qIndex + 1)).get('lid'))
}

function refreshPulseRouteIfReady(): void {
  if (typeof window === 'undefined') return
  const refreshRoute = window.Pulse?.app?.refreshRoute
  if (typeof refreshRoute === 'function') refreshRoute()
}

export function availChartsRouteHasFilterParams(routePath: string): boolean {
  const filters = extractAvailChartsLockFilters(routePath)
  return filters.prodid != null || filters.gnrid != null
}

/**
 * Convert a normalized (scalar) avail-charts-lock route to Pulse's native array-param form.
 * Pulse's AngularJS router serializes route state as `lid[]=…&gnrid[]=…` and its filter
 * handler reads `$routeParams['gnrid[]']` (array key). Passing scalar `gnrid=295001` via
 * `location.href` is stored at `$routeParams.gnrid` which the filter handler does not read.
 * Using array form ensures the genre filter actually applies on hashchange navigation.
 */
export function toAvailChartsLockPulseHash(normalizedRoute: string): string {
  const withSlash = normalizedRoute.startsWith('/') ? normalizedRoute : `/${normalizedRoute}`
  if (!withSlash.startsWith('/reg-flow/avail-charts-lock')) return withSlash
  const qIndex = withSlash.indexOf('?')
  if (qIndex === -1) return withSlash

  const path = withSlash.slice(0, qIndex)
  const params = new URLSearchParams(withSlash.slice(qIndex + 1))
  const result = new URLSearchParams()

  for (const [key, value] of params.entries()) {
    if (key === 'lid') {
      result.set('lid[]', value)
    } else if (key === 'gnrid') {
      result.set('gnrid[]', value)
    } else if (key === 'prodid') {
      result.set('prodid[]', value)
    } else {
      result.set(key, value)
    }
  }

  return `${path}?${result.toString()}`
}

export function shouldActivateLocationAvChartDeepLinkFilters(): boolean {
  if (typeof window === 'undefined') return false
  if (hasRegFlowDeepLinkPending()) return true
  const params = new URLSearchParams(window.location.search)
  return Boolean(params.get('course_id') || params.get('interest'))
}

/**
 * After Pulse sets the chart hash: normalize param shape and copy missing `gnrid` / `prodid`
 * from `data-page` into the hash (hash wins over `data-page` for AVC filter state).
 */
export function tightenRegFlowChartHashInLocationBar(
  options: { dataPage?: string; refreshPulse?: boolean } = {},
): boolean {
  if (typeof window === 'undefined') return false

  const hash = window.location.hash
  if (!hash.startsWith('#/reg-flow/avail-charts-lock')) return false

  const currentRoute = hash.slice(1)
  const targetRoute =
    options.dataPage
    ?? document.getElementById('www-spa-container')?.getAttribute('data-page')
    ?? ''

  const normalizedCurrent = normalizeAvailChartsLockRoute(currentRoute)
  let mergedRoute = normalizedCurrent

  if (targetRoute) {
    const lid =
      parseLidFromAvailChartsRoute(targetRoute)
      ?? parseLidFromAvailChartsRoute(normalizedCurrent)
    mergedRoute = mergeAvailChartsLockFilterRoute(
      normalizedCurrent,
      normalizeAvailChartsLockRoute(
        targetRoute.startsWith('/') ? targetRoute : `/${targetRoute}`,
      ),
      lid,
    )
  }

  if (mergedRoute === normalizedCurrent) return false

  const nextHash = mergedRoute.startsWith('/') ? mergedRoute : `/${mergedRoute}`
  const hashOnly = nextHash.startsWith('#') ? nextHash : `#${nextHash}`

  // Always use replaceState — never location.href. location.href fires a hashchange event
  // which Pulse treats as a new navigation and strips gnrid/prodid from its hash.
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}${hashOnly}`,
  )

  // refreshRoute after replaceState: Pulse re-reads the hash (which now has gnrid) as a soft
  // refresh — no hashchange fired, no full re-init, filter params are preserved.
  if (options.refreshPulse !== false) {
    refreshPulseRouteIfReady()
  }

  return true
}

function resolveFilteredAvailChartsLockDataPage(
  input: LocationAvChartDeepLinkInput,
  lid: number,
  baseDataPage: string,
): Pick<LocationAvChartDeepLinkResult, 'dataPage' | 'appliedCourseId' | 'appliedInterest'> {
  const courseIdCandidate = parsePositiveProductId(input.queryCourseId)
  if (
    courseIdCandidate != null
    && locationOffersProductId(input.locationCourses, courseIdCandidate)
  ) {
    return {
      dataPage: buildAvailChartsLockDataPage(lid, { prodid: courseIdCandidate }),
      appliedCourseId: courseIdCandidate,
      appliedInterest: null,
    }
  }

  const interestCandidate = normalizeInterestName(input.queryInterest)
  if (
    interestCandidate != null
    && locationOffersInterest(input.locationGenres, interestCandidate)
  ) {
    const gnrid = resolveGenreIdForInterest(interestCandidate, input.locationGenres)
    if (gnrid != null) {
      return {
        dataPage: buildAvailChartsLockDataPage(lid, { gnrid }),
        appliedCourseId: null,
        appliedInterest: interestCandidate,
      }
    }
  }

  if (isAvailChartsLockHash(input.hash)) {
    const hashFilters = extractAvailChartsLockFilters(input.hash.startsWith('#') ? input.hash.slice(1) : input.hash)
    if (
      hashFilters.prodid != null
      && locationOffersProductId(input.locationCourses, hashFilters.prodid)
    ) {
      return {
        dataPage: buildAvailChartsLockDataPage(lid, { prodid: hashFilters.prodid }),
        appliedCourseId: hashFilters.prodid,
        appliedInterest: null,
      }
    }
    if (hashFilters.gnrid != null) {
      return {
        dataPage: buildAvailChartsLockDataPage(lid, { gnrid: hashFilters.gnrid }),
        appliedCourseId: null,
        appliedInterest: interestCandidate,
      }
    }
  }

  return {
    dataPage: baseDataPage,
    appliedCourseId: null,
    appliedInterest: null,
  }
}

/** Whether marketing deep-link filters should apply to the embedded chart `data-page`. */
export function isLocationRegFlowChartDeepLink(input: {
  hash?: string
  queryCourseId?: unknown
  queryInterest?: unknown
}): boolean {
  const hash = input.hash ?? ''
  if (isRegFlowHash(hash) || isAvailChartsLockHash(hash)) return true
  if (typeof window !== 'undefined' && hasRegFlowDeepLinkPending()) return true
  if (input.queryCourseId || input.queryInterest) return true
  return false
}

/** Keep `#www-spa-container` `data-page` aligned with Nuxt (Pulse may overwrite it on boot). */
export function syncLocationAvChartDataPage(
  dataPage: string,
  options: { refreshPulse?: boolean } = {},
): boolean {
  if (typeof document === 'undefined' || !dataPage) return false

  const container = document.getElementById('www-spa-container')
  if (!container) return false

  const normalizedTarget = normalizeAvailChartsLockRoute(
    dataPage.startsWith('/') ? dataPage : `/${dataPage}`,
  )
  const current = container.getAttribute('data-page') || ''
  const normalizedCurrent = normalizeAvailChartsLockRoute(
    current.startsWith('/') ? current : `/${current}`,
  )

  const dataPageChanged = normalizedCurrent !== normalizedTarget
  if (dataPageChanged) {
    container.setAttribute('data-page', normalizedTarget)
    sessionStorage.setItem(REG_FLOW_CHART_ROUTE_STORAGE_KEY, normalizedTarget)
  }

  // Skip cosmetic hash tightening while a deep link is pending.
  // tightenRegFlowChartHashInLocationBar converts Pulse's lid[]/gnrid[] array-form params to
  // scalars via replaceState (no hashchange). This makes the hash look like gnrid is applied,
  // which fools the chart detection check into skipping the location.href navigation, even
  // though AngularJS's $location never saw gnrid (replaceState ≠ hashchange). Let Pulse own
  // the hash during deep link init; only tighten for non-deep-link refreshes.
  const hashSynced = !hasRegFlowDeepLinkPending() && tightenRegFlowChartHashInLocationBar({
    dataPage: normalizedTarget,
    refreshPulse: false,
  })

  const hashEmpty = !window.location.hash || window.location.hash === '#'
  const shouldRefresh =
    options.refreshPulse !== false
    // Don't call refreshRoute while deep link is pending — let Pulse apply gnrid from
    // data-page on first load undisturbed. usePulseChartFallback triggers the one
    // controlled refreshRoute after the chart is rendered.
    && !hasRegFlowDeepLinkPending()
    && typeof window !== 'undefined'
    && window.Pulse
    && window.Pulse.app
    && typeof window.Pulse.app.refreshRoute === 'function'
    && (hashEmpty || hashSynced)

  if (shouldRefresh) {
    window.Pulse.app.refreshRoute()
  }

  return dataPageChanged || hashSynced
}

export function resolveLocationAvChartDeepLink(
  input: LocationAvChartDeepLinkInput,
): LocationAvChartDeepLinkResult {
  const lid = parsePositiveProductId(input.locationPulseId)
  const baseDataPage =
    lid != null
      ? buildAvailChartsLockDataPage(lid)
      : '/reg-flow/avail-charts-lock?rgnad=true'

  const isMarketingRegFlow = isRegFlowHash(input.hash)
  const isChartRouteHash = isAvailChartsLockHash(input.hash)
  const inChartDeepLinkContext =
    isMarketingRegFlow
    || isChartRouteHash
    || (typeof window !== 'undefined' && hasRegFlowDeepLinkPending())

  if (!inChartDeepLinkContext || !input.hasEmbeddedChart || lid == null) {
    return {
      isRegFlowDeepLink: isMarketingRegFlow,
      shouldScrollToChart: false,
      dataPage: baseDataPage,
      appliedCourseId: null,
      appliedInterest: null,
    }
  }

  const isDesktopViewport = input.viewportWidth >= LOCATION_AV_CHART_MIN_VIEWPORT_WIDTH
  const shouldScrollToChart =
    isDesktopViewport
    && (isMarketingRegFlow || (typeof window !== 'undefined' && hasRegFlowDeepLinkPending()))

  if (!isDesktopViewport) {
    return {
      isRegFlowDeepLink: true,
      shouldScrollToChart: false,
      dataPage: baseDataPage,
      appliedCourseId: null,
      appliedInterest: null,
    }
  }

  const filtered = resolveFilteredAvailChartsLockDataPage(input, lid, baseDataPage)

  return {
    isRegFlowDeepLink: true,
    shouldScrollToChart,
    ...filtered,
  }
}

export function resolveRegFlowBootstrapDataPage(
  input: Omit<LocationAvChartDeepLinkInput, 'hash' | 'hasEmbeddedChart' | 'viewportWidth'> & {
    viewportWidth?: number
  },
): string {
  return resolveLocationAvChartDeepLink({
    hash: REG_FLOW_HASH,
    hasEmbeddedChart: true,
    viewportWidth: input.viewportWidth ?? LOCATION_AV_CHART_MIN_VIEWPORT_WIDTH,
    queryCourseId: input.queryCourseId,
    queryInterest: input.queryInterest,
    locationPulseId: input.locationPulseId,
    locationCourses: input.locationCourses,
    locationGenres: input.locationGenres,
  }).dataPage
}

export type ScrollToLocationAvChartOptions = {
  showPromoBanner?: boolean
}

/** Scroll to embedded `#www-spa-container`; mirrors existing location CTAs. */
export function scrollToLocationAvChart(options: ScrollToLocationAvChartOptions = {}): boolean {
  if (typeof document === 'undefined') return false

  const container = document.getElementById('www-spa-container')
  if (!container) return false

  let offset = container.offsetTop

  if (options.showPromoBanner) {
    const promoBanner = document.querySelector('.promo-banner') as HTMLElement | null
    if (promoBanner) {
      const promoDeviceOffset = window.innerWidth >= 992 ? -20 : 30
      offset -= promoBanner.offsetHeight + promoDeviceOffset
    }
  }

  const deviceOffset = window.innerWidth >= 992 ? 80 : 0
  const scrollTopTo = offset - 130 - deviceOffset

  window.scrollTo({
    top: scrollTopTo,
    left: 0,
    behavior: 'instant',
  })

  return true
}
